import "./ReviewArea.scss";
import "intersection-observer";

import React, { useCallback, useContext, useRef, useState } from "react";
import { useInViewport } from "react-in-viewport";
import { Transition } from "react-transition-group";

import { ReactComponent as CheckGlyph } from "../assets/check/31px.svg";
import { ReactComponent as RetryGlyph } from "../assets/retry/31px.svg";
import {
  ApplicationPromptCardData,
  BasicCardData,
  CardData,
  CardType,
  PromptData,
} from "../CardData";
import { CardState, UserState } from "../db";
import Login from "../Login";
import { ReviewMarking, SpacedRepetitionSchedule } from "../spacedRepetition";
import Button from "./Button";
import Card, { CardHandle } from "./Card";
import usePointerUpInside from "./hooks/usePointerUpInside";
import usePrevious from "./hooks/usePrevious";
import {
  isScrollBehaviorPolyfillReady,
  useScrollBehaviorPolyfill,
} from "./hooks/useScrollBehaviorPolyfill";
import { ReviewMarkingInteractionState } from "./QuestionProgressIndicator";
import { ReviewContext } from "../util/reviewContext";
import isEqual from "lodash.isequal";

export type QuestionReviewTask = {
  type: "question";
  cardState: CardState | null;
} & (
  | {
      cardData: BasicCardData;
      promptIndex: null;
    }
  | {
      cardData: ApplicationPromptCardData;
      promptIndex: number;
    }
);

export type ReviewAreaMarkingRecord = {
  task: QuestionReviewTask;
  reviewMarking: ReviewMarking;
};

type LoginReviewTask = {
  type: "login";
  userState: UserState;
};

export type ReviewTask = QuestionReviewTask | LoginReviewTask;

interface ReviewAreaProps {
  tasks: ReviewTask[];
  onMark: (
    markingRecord: ReviewAreaMarkingRecord,
    cardHandle: CardHandle,
  ) => void;
  onLogin: () => void;
  schedule: SpacedRepetitionSchedule;
  shouldLabelApplicationPrompts: boolean;

  showsCompletedState?: boolean;
  showsNeedsRetryNotice?: boolean;

  useSlowTransitions?: boolean;

  // Debug flags
  forceShowAnswer?: boolean;
  disableVisibilityTesting?: boolean;
}

function ReviewArea(props: ReviewAreaProps) {
  const {
    tasks,
    onMark,
    onLogin,
    schedule,
    showsCompletedState,
    showsNeedsRetryNotice,
    useSlowTransitions,
    forceShowAnswer,
    disableVisibilityTesting,
    shouldLabelApplicationPrompts,
  } = props;
  const context = useContext(ReviewContext);

  const [isShowingAnswer, setShowingAnswer] = useState(!!forceShowAnswer);
  const lastCommittedReviewMarkingRef = useRef<ReviewAreaMarkingRecord | null>(
    null,
  );
  const previousTasks = usePrevious(props.tasks);

  const [
    pendingMarkingInteractionState,
    setPendingMarkingInteractionState,
  ] = useState<PendingMarkingInteractionState | null>(null);
  const [phase, setPhase] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { inViewport } = useInViewport(
    containerRef,
    { rootMargin: "200px" },
    { disconnectOnLeave: false },
    {},
  );
  useScrollBehaviorPolyfill();

  const currentCardHandleRef = useRef<CardHandle | null>(null);

  const currentTask = tasks[0] || null;
  const onMarkingButton = useCallback(
    (reviewMarking: ReviewMarking) => {
      if (currentTask && currentTask.type === "question") {
        const markingRecord = { task: currentTask, reviewMarking };
        lastCommittedReviewMarkingRef.current = markingRecord;
        onMark(markingRecord, currentCardHandleRef.current!);
      } else {
        throw new Error(`Marked invalid task: ${currentTask}`);
      }
    },
    [currentTask, onMark],
  );

  const onTap = useCallback(() => {
    if (!isShowingAnswer && currentTask && currentTask.type === "question") {
      setShowingAnswer(true);

      const boundingRect = containerRef.current!.getBoundingClientRect();
      if (boundingRect.bottom - 40 > window.innerHeight) {
        if (!isScrollBehaviorPolyfillReady()) {
          console.error("Scroll behavior polyfill still not loaded!");
        }
        window.scrollTo({
          behavior: "smooth",
          top: window.scrollY + (boundingRect.bottom - window.innerHeight) + 45,
          left: 0,
        });
      }
    }
  }, [isShowingAnswer, currentTask]);

  const onToggleTopCardExplanation = useCallback(
    isExplanationExpanded => {
      if (currentTask?.type !== "question") {
        throw new Error(
          "How are we toggling the top card's explanation when it's not a question?",
        );
      }
      context?.onToggleExplanation(
        isExplanationExpanded,
        currentTask.cardData.cardID,
        currentTask.promptIndex,
      );
    },
    [currentTask, context],
  );

  const departingMarkingRecordQueueRef = useRef<ReviewAreaMarkingRecord[]>([]);

  if (
    lastCommittedReviewMarkingRef.current &&
    previousTasks &&
    !isEqual(previousTasks, tasks) &&
    isShowingAnswer
  ) {
    setShowingAnswer(false);
    if (isEqual(previousTasks[1], tasks[0])) {
      departingMarkingRecordQueueRef.current.push(
        lastCommittedReviewMarkingRef.current,
      );
      lastCommittedReviewMarkingRef.current = null;
    }
  }

  const isComplete = tasks.length === 0 && showsCompletedState;

  const maximumCardsToDisplay = window.innerHeight >= 568 ? 3 : 1;
  const maximumCardsToRender = window.innerHeight >= 568 ? 5 : 3;

  type InternalReviewTask =
    | ReviewTask
    | { type: "completedQuestion"; markingRecord: ReviewAreaMarkingRecord };
  const renderedTasks = departingMarkingRecordQueueRef.current
    .map(
      markingRecord =>
        ({
          type: "completedQuestion",
          markingRecord,
        } as InternalReviewTask),
    )
    .concat(tasks)
    .slice(0, maximumCardsToRender);

  return (
    <div
      className={`ReviewArea ${isComplete ? "ReviewAreaComplete" : ""}`}
      style={{
        cursor: isComplete || isShowingAnswer ? undefined : "pointer",
      }}
      ref={containerRef}
      onPointerDown={usePointerUpInside(onTap).onPointerDown}
      touch-action="manipulation"
    >
      <Transition in={isComplete} timeout={500} mountOnEnter unmountOnExit>
        <div
          className="ReviewAreaCompleteSummary"
          style={{ opacity: isComplete ? 1 : 0 }}
        >
          <div className="ReviewAreaCompleteGlyph" />
          <div>Review complete</div>
        </div>
      </Transition>

      <Transition in={!isComplete} timeout={500} mountOnEnter unmountOnExit>
        <div
          className="BottomControlArea"
          style={{ opacity: isComplete ? 0 : 1 }}
        >
          <ReviewButtonArea
            onMark={onMarkingButton}
            onPendingMarkingInteractionStateDidChange={
              setPendingMarkingInteractionState
            }
            disabled={!isShowingAnswer || tasks.length === 0}
            cardType={
              currentTask?.type === "question"
                ? currentTask.cardData.cardType
                : null
            }
          />
        </div>
      </Transition>
      <Transition in={!isComplete} timeout={500} mountOnEnter unmountOnExit>
        <div className="CardContainer" style={{ opacity: isComplete ? 0 : 1 }}>
          {(inViewport || disableVisibilityTesting) &&
            Array.from(new Array(maximumCardsToRender).keys()).map(
              renderNodeIndex => {
                const renderedTaskIndex =
                  (((renderNodeIndex - phase) % maximumCardsToRender) +
                    maximumCardsToRender) %
                  maximumCardsToRender;
                const task: InternalReviewTask | null =
                  renderedTasks[renderedTaskIndex] || null;

                // The rendered stack index is 0 for the card that's currently on top, 1 for the next card down, -1 for the card that's currently animating out.
                const renderedStackIndex =
                  renderedTaskIndex -
                  departingMarkingRecordQueueRef.current.length;
                const isRevealed =
                  (isShowingAnswer && renderedStackIndex === 0) ||
                  renderedStackIndex < 0;

                let cardComponent: React.ReactNode;

                if (task === null) {
                  cardComponent = null;
                } else if (task.type === "login") {
                  cardComponent = (
                    <div className="Card LoginCard">
                      <Login
                        isActive={renderedStackIndex === 0}
                        onLogin={onLogin}
                        userState={task.userState}
                      />
                    </div>
                  );
                } else {
                  let cardData: CardData;
                  let cardState: CardState | null;
                  let reviewMarkingInteractionState: ReviewMarkingInteractionState | null;
                  let promptData: PromptData;

                  if (task.type === "question") {
                    cardData = task.cardData;
                    cardState = task.cardState;
                    if (
                      lastCommittedReviewMarkingRef.current &&
                      lastCommittedReviewMarkingRef.current.task.cardData
                        .cardID === cardData.cardID
                    ) {
                      reviewMarkingInteractionState = {
                        status: "committed",
                        reviewMarking:
                          lastCommittedReviewMarkingRef.current.reviewMarking,
                      } as const;
                    } else if (
                      renderedStackIndex === 0 &&
                      pendingMarkingInteractionState !== null &&
                      isShowingAnswer
                    ) {
                      reviewMarkingInteractionState = {
                        status:
                          pendingMarkingInteractionState.status === "hover"
                            ? "pending"
                            : "committed",
                        reviewMarking:
                          pendingMarkingInteractionState.pendingReviewMarking,
                      } as const;
                    } else {
                      reviewMarkingInteractionState = null;
                    }
                    promptData = getPromptDataFromQuestionReviewTask(task);
                  } else {
                    cardData = task.markingRecord.task.cardData;
                    cardState = task.markingRecord.task.cardState;
                    promptData = getPromptDataFromQuestionReviewTask(
                      task.markingRecord.task,
                    );
                    reviewMarkingInteractionState = {
                      status: "committed",
                      reviewMarking: task.markingRecord.reviewMarking,
                    };
                  }

                  cardComponent = (
                    <Card
                      isRevealed={isRevealed}
                      isOccluded={renderedStackIndex > 0}
                      promptData={promptData}
                      cardState={cardState}
                      cardType={cardData.cardType}
                      reviewMarkingInteractionState={
                        reviewMarkingInteractionState
                      }
                      schedule={schedule}
                      showsNeedsRetryNotice={showsNeedsRetryNotice}
                      ref={
                        renderedStackIndex === 0
                          ? currentCardHandleRef
                          : undefined
                      }
                      onToggleExplanation={onToggleTopCardExplanation}
                      shouldLabelApplicationPrompts={
                        shouldLabelApplicationPrompts
                      }
                    />
                  );
                }

                let containerStyle: React.CSSProperties = {};
                if (
                  task &&
                  (task.type === "login" || task.type === "question")
                ) {
                  const scale = 1 - renderedStackIndex * 0.05;
                  const offset = renderedStackIndex * 20;
                  containerStyle = {
                    transform: isRevealed ? "" : `scale(${scale}, ${scale})`,
                    "--offset": isRevealed ? "0px" : `${offset}px`,
                  } as React.CSSProperties;
                }

                const isDisplayed =
                  task &&
                  task.type !== "completedQuestion" &&
                  renderedStackIndex < maximumCardsToDisplay;

                containerStyle.opacity = isDisplayed ? 1 : 0;
                containerStyle.zIndex =
                  maximumCardsToDisplay - renderedStackIndex + 1;

                return (
                  <Transition
                    in={isDisplayed}
                    key={renderNodeIndex}
                    onExited={() => {
                      departingMarkingRecordQueueRef.current.splice(
                        -1 * renderedStackIndex - 1,
                        1,
                      );
                      setPhase(phase => phase + 1);
                    }}
                    timeout={1500}
                  >
                    <div
                      className={`CardDynamicsContainer
                    ${
                      task && task.type === "completedQuestion"
                        ? "Disappearing"
                        : ""
                    } ${useSlowTransitions ? "DelayTransitions" : ""}`}
                      style={containerStyle}
                    >
                      {cardComponent}
                    </div>
                  </Transition>
                );
              },
            )}
        </div>
      </Transition>
    </div>
  );
}

export default ReviewArea;

interface PendingMarkingInteractionState {
  pendingReviewMarking: ReviewMarking;
  status: "hover" | "active";
}

function ReviewButtonArea(props: {
  onMark: (reviewMarking: ReviewMarking) => void;
  onPendingMarkingInteractionStateDidChange: (
    state: PendingMarkingInteractionState | null,
  ) => void;
  disabled: boolean;
  cardType: CardType | null;
}) {
  const {
    onMark,
    onPendingMarkingInteractionStateDidChange,
    disabled,
    cardType,
  } = props;
  return (
    <div className="ReviewButtonArea">
      <Button
        variant="primary"
        size="regular"
        onActivate={useCallback(() => onMark("forgotten"), [onMark])}
        onStatusDidChange={useCallback(
          status =>
            onPendingMarkingInteractionStateDidChange(
              status === "inactive"
                ? null
                : {
                    status,
                    pendingReviewMarking: "forgotten",
                  },
            ),
          [onPendingMarkingInteractionStateDidChange],
        )}
        glyph={<RetryGlyph />}
        disabled={disabled}
      >
        {cardType === "basic" || null ? "Didn’t remember" : "Couldn’t answer"}
      </Button>
      <Button
        variant="primary"
        size="regular"
        onActivate={useCallback(() => onMark("remembered"), [onMark])}
        onStatusDidChange={useCallback(
          status =>
            onPendingMarkingInteractionStateDidChange(
              status === "inactive"
                ? null
                : {
                    status,
                    pendingReviewMarking: "remembered",
                  },
            ),
          [onPendingMarkingInteractionStateDidChange],
        )}
        glyph={<CheckGlyph />}
        disabled={disabled}
      >
        {cardType === "basic" || null ? "Remembered" : "Answered"}
      </Button>
    </div>
  );
}

function getPromptDataFromQuestionReviewTask(task: QuestionReviewTask) {
  const cardData: CardData = task.cardData;
  if (cardData.cardType === "basic") {
    return cardData;
  } else {
    if (task.promptIndex === null) {
      throw new Error(
        "Should be unreachable, but Typescript's narrowing isn't smart enough.",
      );
    }
    return cardData.prompts[task.promptIndex];
  }
}
